import { configureStore } from '@reduxjs/toolkit';

import {
  accessApi,
  agreementsApi,
  assignmentApi,
  auditLogsApi,
  authLoginApi,
  businessOwnershipApi,
  companyLicensesApi,
  conciergeApi,
  contractorApi,
  contractorCredentialingApi,
  coverageAreaApi,
  dashboardChartsApi,
  dashboardTablesApi,
  documentsApi,
  driverChatApi,
  globalApi,
  liabilityApi,
  locationsApi,
  loginApi,
  membersApi,
  modeOfTransportApi,
  monitoringApi,
  notesApi,
  payorsApi,
  personnelApi,
  placesApi,
  ridesApi,
  selectsApi,
  serviceApi,
  sliderApi,
  surveyTemplateApi,
  twilioApi,
  usersApi,
  vehicleApi,
} from './rtk';
import {
  accessReducer,
  agreementsReducer,
  assignmentReducer,
  auditLogsReducer,
  authContactsReducer,
  benefitsReducer,
  businessOwnershipReducer,
  companyLicensesReducer,
  contractorInfoReducer,
  contractorPortalInvitationsReducer,
  coverageAreaReducer,
  documentsReducer,
  driverChatReducer,
  exclusionContractorsReducer,
  globalReducer,
  homeSearchReducer,
  liabilityReducer,
  locationsReducer,
  lyftReducer,
  membersContactsReducer,
  membersInfoReducer,
  modeOfTransportReducer,
  monitoringReducer,
  notesReducer,
  notificationReducer,
  payorsAuditReducer,
  payorsInfoReducer,
  payorsServiceReducer,
  personnelReducer,
  placesInfoReducer,
  placesLocationReducer,
  placesServicesReducer,
  preferencesReducer,
  qualityIncidentsReducer,
  ratesReducer,
  ridesReducer,
  ridesTableReducer,
  selectsReducer,
  surveysReducer,
  tagsReducer,
  usersReducer,
  vehicleReducer,
} from '@store/slices';
import { reportsReducers } from './slices/report-dashboard/report-dashboard';
import { injectStore } from '@utils/logger';

export const store = configureStore({
  reducer: {
    /*** API Reducers ***/
    [accessApi.reducerPath]: accessApi.reducer,
    [agreementsApi.reducerPath]: agreementsApi.reducer,
    [auditLogsApi.reducerPath]: auditLogsApi.reducer,
    [authLoginApi.reducerPath]: authLoginApi.reducer,
    [assignmentApi.reducerPath]: assignmentApi.reducer,
    [monitoringApi.reducerPath]: monitoringApi.reducer,
    [businessOwnershipApi.reducerPath]: businessOwnershipApi.reducer,
    [companyLicensesApi.reducerPath]: companyLicensesApi.reducer,
    [contractorApi.reducerPath]: contractorApi.reducer,
    [coverageAreaApi.reducerPath]: coverageAreaApi.reducer,
    [documentsApi.reducerPath]: documentsApi.reducer,
    [driverChatApi.reducerPath]: driverChatApi.reducer,
    [globalApi.reducerPath]: globalApi.reducer,
    [liabilityApi.reducerPath]: liabilityApi.reducer,
    [locationsApi.reducerPath]: locationsApi.reducer,
    [loginApi.reducerPath]: loginApi.reducer,
    [membersApi.reducerPath]: membersApi.reducer,
    [modeOfTransportApi.reducerPath]: modeOfTransportApi.reducer,
    [notesApi.reducerPath]: notesApi.reducer,
    [payorsApi.reducerPath]: payorsApi.reducer,
    [contractorCredentialingApi.reducerPath]:
      contractorCredentialingApi.reducer,
    [personnelApi.reducerPath]: personnelApi.reducer,
    [placesApi.reducerPath]: placesApi.reducer,
    [ridesApi.reducerPath]: ridesApi.reducer,
    [dashboardChartsApi.reducerPath]: dashboardChartsApi.reducer,
    [dashboardTablesApi.reducerPath]: dashboardTablesApi.reducer,

    [selectsApi.reducerPath]: selectsApi.reducer,
    [serviceApi.reducerPath]: serviceApi.reducer,
    [sliderApi.reducerPath]: sliderApi.reducer,
    [surveyTemplateApi.reducerPath]: surveyTemplateApi.reducer,
    [twilioApi.reducerPath]: twilioApi.reducer,
    [usersApi.reducerPath]: usersApi.reducer,
    [vehicleApi.reducerPath]: vehicleApi.reducer,

    /*** lyft API start ***/
    [conciergeApi.reducerPath]: conciergeApi.reducer,

    /*** lyft API end ***/
    /*** Reducers ***/
    access: accessReducer,
    reportDashboard: reportsReducers,
    agreements: agreementsReducer,
    assignment: assignmentReducer,
    monitoring: monitoringReducer,
    auditLogs: auditLogsReducer,
    authContacts: authContactsReducer,
    benefits: benefitsReducer,
    businessOwnership: businessOwnershipReducer,
    companyLicenses: companyLicensesReducer,
    contractorInfo: contractorInfoReducer,
    contractorPortalInvitations: contractorPortalInvitationsReducer,
    contractorTags: tagsReducer,
    coverageArea: coverageAreaReducer,
    documents: documentsReducer,
    driverChat: driverChatReducer,
    exclusionContractors: exclusionContractorsReducer,
    global: globalReducer,
    homeSearch: homeSearchReducer,
    liability: liabilityReducer,
    locations: locationsReducer,
    lyftConfigs: lyftReducer,
    membersContacts: membersContactsReducer,
    membersInfo: membersInfoReducer,
    modeOfTransport: modeOfTransportReducer,
    notes: notesReducer,
    notifications: notificationReducer,
    payorsAudit: payorsAuditReducer,
    payorsInfo: payorsInfoReducer,
    payorsServices: payorsServiceReducer,
    personnel: personnelReducer,
    placesInfo: placesInfoReducer,
    placesLocation: placesLocationReducer,
    preferences: preferencesReducer,
    qualityIncidents: qualityIncidentsReducer,
    rates: ratesReducer,
    rides: ridesReducer,
    ridesTable: ridesTableReducer,
    services: placesServicesReducer,
    selects: selectsReducer,
    surveys: surveysReducer,
    users: usersReducer,
    vehicle: vehicleReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    })
      .concat(accessApi.middleware)
      .concat(agreementsApi.middleware)
      .concat(assignmentApi.middleware)
      .concat(auditLogsApi.middleware)
      .concat(authLoginApi.middleware)
      .concat(businessOwnershipApi.middleware)
      .concat(companyLicensesApi.middleware)
      .concat(conciergeApi.middleware)
      .concat(contractorApi.middleware)
      .concat(coverageAreaApi.middleware)
      .concat(dashboardChartsApi.middleware)
      .concat(dashboardTablesApi.middleware)
      .concat(documentsApi.middleware)
      .concat(driverChatApi.middleware)
      .concat(globalApi.middleware)
      .concat(liabilityApi.middleware)
      .concat(locationsApi.middleware)
      .concat(loginApi.middleware)
      .concat(membersApi.middleware)
      .concat(modeOfTransportApi.middleware)
      .concat(monitoringApi.middleware)
      .concat(notesApi.middleware)
      .concat(payorsApi.middleware)
      .concat(contractorCredentialingApi.middleware)
      .concat(personnelApi.middleware)
      .concat(placesApi.middleware)
      .concat(ridesApi.middleware)
      .concat(selectsApi.middleware)
      .concat(serviceApi.middleware)
      .concat(sliderApi.middleware)
      .concat(surveyTemplateApi.middleware)
      .concat(twilioApi.middleware)
      .concat(usersApi.middleware)
      .concat(vehicleApi.middleware),
});

injectStore(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;

export default store;
